import {
  ArrowLeft,
  Check,
  Eye,
  Image as ImageIcon,
  RefreshCcw,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  cleanupNovaMediaOrphans,
  discardNovaMediaDraft,
  loadNovaMediaSlots,
  publishNovaMediaSlot,
  removeNovaPublishedMedia,
  uploadNovaMediaDraft,
  type NovaMediaSlot,
} from "../media/nova-media";

function MediaPreview({
  url,
  mimeType,
  label,
}: {
  url: string | null;
  mimeType: string | null;
  label: string;
}) {
  if (!url) {
    return (
      <div className="flex aspect-[16/10] items-center justify-center border border-dashed border-[#b5bbae] bg-[#eee9dc] px-6 text-center">
        <div>
          <ImageIcon
            className="mx-auto text-[#9d725c]"
            size={24}
            strokeWidth={1.2}
          />

          <p className="mt-4 text-[9px] uppercase tracking-[0.18em] text-[#718076]">
            No media yet
          </p>
        </div>
      </div>
    );
  }

  if (
    mimeType?.startsWith(
      "video/",
    )
  ) {
    return (
      <video
        src={url}
        controls
        muted
        playsInline
        className="aspect-[16/10] w-full bg-[#26392c] object-contain"
      />
    );
  }

  return (
    <img
      src={url}
      alt={label}
      className="aspect-[16/10] w-full bg-[#eee9dc] object-contain"
    />
  );
}

export function NovaCmsMediaManager() {
  const [
    slots,
    setSlots,
  ] =
    useState<
      NovaMediaSlot[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    busyKey,
    setBusyKey,
  ] =
    useState<
      string | null
    >(null);

  const [
    message,
    setMessage,
  ] =
    useState<
      string | null
    >(null);

  const [
    error,
    setError,
  ] =
    useState<
      string | null
    >(null);

  const load =
    useCallback(
      async () => {
        setLoading(true);
        setError(null);

        try {
          setSlots(
            await loadNovaMediaSlots(),
          );
        } catch (
          loadError
        ) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Media could not be loaded.",
          );
        }

        setLoading(false);
      },
      [],
    );

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(
    slot: NovaMediaSlot,
    file: File,
  ) {
    setBusyKey(
      slot.slot_key,
    );
    setMessage(null);
    setError(null);

    try {
      await uploadNovaMediaDraft(
        slot,
        file,
      );

      setMessage(
        `${slot.label} draft uploaded. The live website has not changed.`,
      );

      await load();
    } catch (
      uploadError
    ) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Media upload failed.",
      );
    }

    setBusyKey(null);
  }

  async function publish(
    slot: NovaMediaSlot,
  ) {
    setBusyKey(
      slot.slot_key,
    );
    setMessage(null);
    setError(null);

    try {
      await publishNovaMediaSlot(
        slot,
      );

      setMessage(
        `${slot.label} is now live.`,
      );

      await load();
    } catch (
      publishError
    ) {
      setError(
        publishError instanceof Error
          ? publishError.message
          : "Media could not be published.",
      );
    }

    setBusyKey(null);
  }

  async function discard(
    slot: NovaMediaSlot,
  ) {
    setBusyKey(
      slot.slot_key,
    );
    setMessage(null);
    setError(null);

    try {
      await discardNovaMediaDraft(
        slot,
      );

      setMessage(
        `${slot.label} draft discarded. Published media was not changed.`,
      );

      await load();
    } catch (
      discardError
    ) {
      setError(
        discardError instanceof Error
          ? discardError.message
          : "Draft could not be discarded.",
      );
    }

    setBusyKey(null);
  }

  async function cleanUnusedMedia() {
    const confirmed =
      window.confirm(
        "Clean unused Nova media files?\n\nOnly Storage files that are no longer referenced by any current draft or published media slot will be permanently deleted.",
      );

    if (!confirmed) {
      return;
    }

    setBusyKey(
      "__cleanup__",
    );

    setMessage(null);
    setError(null);

    try {
      const currentSlots =
        await loadNovaMediaSlots();

      const result =
        await cleanupNovaMediaOrphans(
          currentSlots,
        );

      if (
        result.totalRemoved ===
        0
      ) {
        setMessage(
          "Storage is clean. No unused media files were found.",
        );
      } else {
        setMessage(
          `Storage cleanup complete. ${result.totalRemoved} unused file${result.totalRemoved === 1 ? "" : "s"} permanently removed.`,
        );
      }

      await load();
    } catch (
      cleanupError
    ) {
      setError(
        cleanupError instanceof Error
          ? cleanupError.message
          : "Storage cleanup failed.",
      );
    }

    setBusyKey(null);
  }

  async function removePublished(
    slot: NovaMediaSlot,
  ) {
    const confirmed =
      window.confirm(
        `Remove published media from "${slot.label}"?\n\nThe storage file will be permanently deleted. The website will return to its fallback/default media where one exists.`,
      );

    if (!confirmed) {
      return;
    }

    setBusyKey(
      slot.slot_key,
    );

    setMessage(null);
    setError(null);

    try {
      await removeNovaPublishedMedia(
        slot,
      );

      setMessage(
        `${slot.label} published media removed permanently.`,
      );

      await load();
    } catch (
      removeError
    ) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Published media could not be removed.",
      );
    }

    setBusyKey(null);
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#53685b] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-5 py-5 sm:px-8">
          <button
            type="button"
            onClick={() => {
              window.location.href =
                "/nova-hq";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em]"
          >
            <ArrowLeft
              size={14}
            />
            Nova HQ
          </button>

          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#d6bd91]"
          >
            <Eye size={14} />
            View live website
          </a>
        </div>
      </header>

      <section className="mx-auto max-w-[1500px] px-5 py-10 sm:px-8 lg:py-14">
        <div className="flex flex-col justify-between gap-7 border-b border-[#b5bbae] pb-9 lg:flex-row lg:items-end">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
              Media
            </p>

            <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none sm:text-6xl">
              What Nova looks like.
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
              Upload a replacement,
              preview it privately,
              then publish only when
              it is ready. Uploading a
              draft never changes the
              public website.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() =>
                void load()
              }
              disabled={
                busyKey !== null
              }
              className="flex h-11 items-center gap-2 border border-[#9d725c] px-5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c] disabled:opacity-50"
            >
              <RefreshCcw
                size={14}
              />
              Refresh
            </button>

            <button
              type="button"
              onClick={() =>
                void cleanUnusedMedia()
              }
              disabled={
                busyKey !== null
              }
              className="flex h-11 items-center gap-2 bg-[#314a3b] px-5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#f3ead7] disabled:opacity-50"
            >
              <Trash2
                size={14}
              />
              Clean unused files
            </button>
          </div>
        </div>

        {message && (
          <div className="mt-6 flex items-center gap-3 border border-[#718076]/30 bg-[#dfe3d7] px-5 py-4 text-xs">
            <Check
              size={15}
            />
            {message}
          </div>
        )}

        {error && (
          <div className="mt-6 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs text-[#7d5544]">
            {error}
          </div>
        )}

        {loading ? (
          <p className="mt-10 text-[10px] uppercase tracking-[0.2em] text-[#718076]">
            Loading media…
          </p>
        ) : (
          <div className="mt-8 space-y-6">
            {slots.map(
              (slot) => {
                const busy =
                  busyKey ===
                  slot.slot_key;

                return (
                  <article
                    key={
                      slot.slot_key
                    }
                    className="border border-[#b5bbae] bg-[#f4efe4] p-6 sm:p-8"
                  >
                    <div className="flex flex-col justify-between gap-5 border-b border-[#b5bbae] pb-6 sm:flex-row sm:items-end">
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                          {
                            slot.section_key
                          }
                        </p>

                        <h2 className="mt-3 font-['Cormorant_Garamond'] text-3xl">
                          {
                            slot.label
                          }
                        </h2>

                        <p className="mt-2 text-[9px] uppercase tracking-[0.16em] text-[#8d988d]">
                          {
                            slot.slot_key
                          }
                          {" · "}
                          {
                            slot.media_kind
                          }
                        </p>
                      </div>

                      <label className="inline-flex h-11 cursor-pointer items-center gap-2 border border-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em]">
                        <Upload
                          size={13}
                        />

                        Upload replacement

                        <input
                          type="file"
                          accept={
                            slot.media_kind === "video"
                              ? "video/mp4,video/webm"
                              : "image/jpeg,image/png,image/webp,image/gif"
                          }
                          className="hidden"
                          disabled={
                            busy
                          }
                          onChange={(
                            event,
                          ) => {
                            const file =
                              event
                                .target
                                .files?.[0];

                            event.currentTarget.value =
                              "";

                            if (file) {
                              void upload(
                                slot,
                                file,
                              );
                            }
                          }}
                        />
                      </label>
                    </div>

                    <div className="mt-7 grid gap-6 lg:grid-cols-2">
                      <div>
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076]">
                            Published
                          </p>

                          {slot.published_path && (
                            <span className="text-[8px] uppercase tracking-[0.15em] text-[#718076]">
                              Live
                            </span>
                          )}
                        </div>

                        <MediaPreview
                          url={
                            slot.published_url
                          }
                          mimeType={
                            slot.published_mime_type
                          }
                          label={
                            `${slot.label} published`
                          }
                        />

                        <p className="mt-3 truncate text-[10px] text-[#667067]">
                          {
                            slot.published_file_name ??
                            "Using website fallback"
                          }
                        </p>

                        {slot.published_path && (
                          <button
                            type="button"
                            disabled={
                              busy
                            }
                            onClick={() =>
                              void removePublished(
                                slot,
                              )
                            }
                            className="mt-4 flex h-10 items-center gap-2 border border-[#9d725c]/45 px-3 text-[8px] font-semibold uppercase tracking-[0.14em] text-[#9d725c] disabled:opacity-50"
                          >
                            <Trash2
                              size={12}
                            />
                            Remove published media
                          </button>
                        )}
                      </div>

                      <div>
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                            Draft preview
                          </p>

                          {slot.draft_path && (
                            <span className="text-[8px] uppercase tracking-[0.15em] text-[#9d725c]">
                              Not live
                            </span>
                          )}
                        </div>

                        <MediaPreview
                          url={
                            slot.draft_url
                          }
                          mimeType={
                            slot.draft_mime_type
                          }
                          label={
                            `${slot.label} draft`
                          }
                        />

                        <p className="mt-3 truncate text-[10px] text-[#667067]">
                          {
                            slot.draft_file_name ??
                            "No draft uploaded"
                          }
                        </p>
                      </div>
                    </div>

                    {slot.draft_path && (
                      <div className="mt-6 flex flex-wrap justify-end gap-3 border-t border-[#b5bbae] pt-6">
                        <button
                          type="button"
                          disabled={
                            busy
                          }
                          onClick={() =>
                            void discard(
                              slot,
                            )
                          }
                          className="flex h-11 items-center gap-2 border border-[#9d725c] px-4 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#9d725c] disabled:opacity-50"
                        >
                          <Trash2
                            size={13}
                          />
                          Discard draft
                        </button>

                        <button
                          type="button"
                          disabled={
                            busy
                          }
                          onClick={() =>
                            void publish(
                              slot,
                            )
                          }
                          className="flex h-11 items-center gap-2 bg-[#314a3b] px-5 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7] disabled:opacity-50"
                        >
                          <Send
                            size={13}
                          />
                          Publish media
                        </button>
                      </div>
                    )}
                  </article>
                );
              },
            )}
          </div>
        )}
      </section>
    </main>
  );
}
